import { getSupabase } from '@/lib/supabase';
import type { ClientGoal, GoalType } from '@/types/goals';
import type { GoalProgress } from './index';

// Map UI goal types to database enum values
function mapGoalTypeToDb(uiType: string): string {
  const mapping: Record<string, string> = {
    weight: 'weight_loss',
    habit: 'habit',
    performance: 'strength', // Map performance to strength
    muscle: 'muscle_gain',
    strength: 'strength',
    endurance: 'endurance',
    custom: 'custom',
  };
  return mapping[uiType] || 'custom';
}

// Map database enum values to UI goal types
function mapGoalTypeFromDb(dbType: string): string {
  const mapping: Record<string, string> = {
    weight_loss: 'weight',
    muscle_gain: 'weight', // Map muscle gain back to weight for UI
    strength: 'performance', // Map strength to performance for UI
    endurance: 'performance', // Map endurance to performance for UI
    habit: 'habit',
    custom: 'custom',
  };
  return mapping[dbType] || 'custom';
}

// Map database row to ClientGoal interface
function mapGoalFromDb(row: any): ClientGoal {
  return {
    id: row.id,
    clientId: row.client_id,
    ownerCoachId: row.owner_coach_id || undefined, // Handle optional property
    linkedTrackerId: row.linked_tracker_id ?? null,
    title: row.title,
    type: mapGoalTypeFromDb(row.type) as GoalType,
    unit: row.unit,
    targetValue: row.target_value !== null && row.target_value !== undefined ? parseFloat(row.target_value) : 0,
    currentValue: row.current_value !== null && row.current_value !== undefined ? parseFloat(row.current_value) : 0,
    deadline: row.deadline,
    priority: row.priority,
    description: row.description,
    shareWithCoach: row.share_with_coach,
    status: row.status,
    progress: parseFloat(row.progress || 0),
    schedule: row.schedule,
    lastCheckinAtISO: row.last_checkin_at, // Use correct property name
    nextDueOn: row.next_due_on,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    history: [], // Initialize empty history
  };
}

// Map ClientGoal to database row
function mapGoalToDb(goal: Partial<ClientGoal>): any {
  return {
    id: goal.id,
    client_id: goal.clientId,
    owner_coach_id: goal.ownerCoachId,
    linked_tracker_id: goal.linkedTrackerId ?? null,
    title: goal.title,
    type: goal.type ? mapGoalTypeToDb(goal.type) : undefined,
    unit: goal.unit,
    target_value: goal.targetValue,
    current_value: goal.currentValue,
    initial_value: goal.currentValue, // Set initial_value to current_value on creation
    deadline: goal.deadline,
    priority: goal.priority,
    description: goal.description,
    share_with_coach: goal.shareWithCoach,
    status: goal.status,
    progress: goal.progress,
    schedule: goal.schedule,
    last_checkin_at: goal.lastCheckinAtISO, // Use correct property name
    next_due_on: goal.nextDueOn,
  };
}

export async function listGoals(clientId: string): Promise<ClientGoal[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_goals')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapGoalFromDb);
}

export async function createGoal(goal: Omit<ClientGoal, 'id' | 'createdAt' | 'updatedAt'>): Promise<ClientGoal> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const dbGoal = mapGoalToDb(goal);
  delete dbGoal.id; // Let database generate UUID

  // If owner_coach_id is not provided, look it up from coach_clients table
  if (!dbGoal.owner_coach_id && dbGoal.client_id) {
    const { data: coachClient, error: lookupError } = await sb
      .from('coach_clients')
      .select('coach_id')
      .eq('client_id', dbGoal.client_id)
      .limit(1)
      .maybeSingle();

    if (lookupError) {
      throw new Error('Could not find coach for client. Please contact support.');
    }

    if (coachClient) {
      dbGoal.owner_coach_id = coachClient.coach_id;
    } else {
      throw new Error('No coach found for this client. Please ensure you are linked to a coach.');
    }
  }

  const { data, error } = await sb.from('client_goals').insert(dbGoal).select().single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function updateGoal(goalId: string, updates: Partial<ClientGoal>): Promise<ClientGoal> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const dbUpdates = mapGoalToDb(updates);
  delete dbUpdates.id; // Don't update the ID

  const { data, error } = await sb.from('client_goals').update(dbUpdates).eq('id', goalId).select().single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function deleteGoal(goalId: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { error } = await sb.from('client_goals').delete().eq('id', goalId);

  if (error) throw error;
}

export async function addGoalProgress(
  goalId: string,
  progress: { value: number; note?: string },
): Promise<GoalProgress> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_goal_progress')
    .insert({
      goal_id: goalId,
      value: progress.value,
      note: progress.note,
    })
    .select()
    .single();

  if (error) throw error;
  return {
    id: data.id,
    goalId: data.goal_id,
    value: parseFloat(data.value),
    note: data.note,
    createdAt: data.created_at,
    createdBy: data.created_by,
  };
}

export async function listGoalProgress(goalId: string): Promise<GoalProgress[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_goal_progress')
    .select('*')
    .eq('goal_id', goalId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    goalId: row.goal_id,
    value: parseFloat(row.value),
    note: row.note,
    createdAt: row.created_at,
    createdBy: row.created_by,
  }));
}

export async function getCoachGoalsForClient(clientId: string, coachId: string): Promise<ClientGoal[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_goals')
    .select('*')
    .eq('client_id', clientId)
    .eq('owner_coach_id', coachId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapGoalFromDb);
}

// Export the goals store interface for adapter compatibility
export const supabaseGoalsStore = {
  async getClientGoals(clientId: string): Promise<ClientGoal[]> {
    return listGoals(clientId);
  },
  async setClientGoals(clientId: string, goals: ClientGoal[]): Promise<void> {
    // This method is not typically used in Supabase mode since we don't batch update
    // But we can implement it by clearing and recreating goals
    try {
      const existingGoals = await listGoals(clientId);
      for (const goal of existingGoals) {
        await deleteGoal(goal.id);
      }
      for (const goal of goals) {
        await createGoal(goal);
      }
    } catch (error) {
      console.error('Failed to set client goals:', error);
      throw error;
    }
  },
  // Additional methods for GlobalStateProvider compatibility
  listGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  addGoalProgress,
  listGoalProgress,
  getCoachGoalsForClient,
};
